<template>
  <div
    v-if="show"
    class="modal modal-open top-anchored-modal"
  >
    <div
      class="modal-box relative"
      :class="isGridMode ? 'max-w-[95vw] w-[95vw] p-0 overflow-hidden grid-mode-active' : 'pb-24'"
      :style="isGridMode
        ? { 'max-height': 'calc(100vh - 1rem)', 'height': '98vh' }
        : { 'max-height': 'calc(100vh - 5rem)', 'overflow-y': apiKeysDropdownOpen ? 'visible' : 'auto' }
      "
    >
      <!-- Header (always visible) -->
      <div :class="isGridMode ? 'px-5 py-3 border-b border-base-300 shrink-0' : ''">
        <button
          class="btn btn-sm btn-circle absolute right-2 top-2"
          style="z-index: 10;"
          @click="$emit('close')"
        >
          ✕
        </button>
        <h3
          class="font-bold text-lg pr-10 truncate max-w-full overflow-hidden"
          :class="[
            { 'tooltip tooltip-bottom': isEditing },
            isGridMode ? 'mb-0' : 'mb-4'
          ]"
          :data-tip="isEditing ? `Edit: ${botToEdit?.name}` : ''"
        >
          {{ isEditing ? `Edit: ${botToEdit?.name || 'Bot'}` : "Create New Bot" }}
        </h3>
        <div
          v-if="isEditing && !isGridMode"
          class="text-sm text-base-content/70 mb-4"
        >
          {{ form.account_name || "No account" }} •
          <span v-if="botToEdit && botToEdit.api_keys && botToEdit.api_keys.length > 0">
            {{ getApiKeyDescriptions() }}
          </span>
          <span v-else>
            {{ form.api_key_description || "No API keys" }}
          </span>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="isInitializing"
        class="flex flex-col items-center justify-center py-12"
      >
        <span class="loading loading-spinner loading-lg mb-4" />
        <p class="text-base-content/70">
          Loading bot configuration...
        </p>
      </div>

      <!-- Main Content (hidden while loading) -->
      <div
        v-else
        class="flex flex-1 min-h-0"
        :class="isGridMode ? 'flex-col md:flex-row' : 'flex-col'"
        :style="isGridMode ? { height: 'calc(100% - 52px)' } : {}"
      >
        <!-- LEFT PANEL: Range Explorer Chart (grid mode only) -->
        <div
          v-if="isGridMode"
          class="hidden md:flex flex-[7] flex-col min-w-0 border-r border-base-300 overflow-y-auto"
        >
          <RangeExplorerPreview
            ref="gridPreview"
            :symbol="form.symbol"
            :trading-mode="gridTradingMode"
            :defaults="gridDefaults"
            :hide-stats="true"
            @config-updated="handleGridConfigUpdated"
          />
        </div>

        <!-- Mobile: Chart toggle (grid mode only) -->
        <div
          v-if="isGridMode"
          class="md:hidden px-3 pt-2 shrink-0"
        >
          <button
            type="button"
            class="btn btn-sm btn-outline btn-block"
            @click="showGridChartMobile = !showGridChartMobile"
          >
            {{ showGridChartMobile ? 'Hide Chart' : 'Show Chart' }}
          </button>
          <div
            v-if="showGridChartMobile"
            class="mt-2"
          >
            <RangeExplorerPreview
              ref="gridPreviewMobile"
              :symbol="form.symbol"
              :trading-mode="gridTradingMode"
              :defaults="gridDefaults"
              :hide-stats="true"
              @config-updated="handleGridConfigUpdated"
            />
          </div>
        </div>

        <!-- RIGHT PANEL (grid mode) / SINGLE PANEL (non-grid mode) -->
        <div
          :class="isGridMode ? 'flex-[3] flex flex-col min-w-0 overflow-y-auto p-4' : ''"
        >
          <!-- Tabs -->
          <div class="mb-4">
            <div class="tabs tabs-boxed">
              <a
                v-for="tab in tabs"
                :key="tab"
                class="tab flex-1"
                :class="{ 'tab-active': activeTab === tab }"
                @click="activeTab = tab"
              >
                {{ tab }}
              </a>
            </div>
          </div>

          <!-- Top Action Buttons (Edit mode only) -->
          <div
            v-if="isEditing"
            class="grid grid-cols-2 gap-2 mb-4 pb-4 border-b border-base-300"
          >
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="isSubmitting"
              @click="submitFormKeepOpen"
            >
              {{ isSubmitting ? "Updating..." : "Update" }}
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="isSubmitting"
              @click="submitForm"
            >
              {{ isSubmitting ? "Updating..." : "Update & Close" }}
            </button>
          </div>

          <form
            class="space-y-4"
            @submit.prevent="submitForm"
          >
            <!-- Grid Tab (grid mode only) -->
            <div
              v-if="isGridMode"
              v-show="activeTab === 'Grid'"
            >
              <div class="flex items-center justify-between mb-3">
                <div class="text-sm text-base-content/70">
                  {{ gridCoupled ? 'Changes auto-sync to Strategy fields.' : 'Strategy fields are decoupled — manual edits won\'t be overwritten.' }}
                </div>
                <label class="label cursor-pointer gap-2 shrink-0">
                  <span class="label-text text-xs">Sync</span>
                  <input
                    v-model="gridCoupled"
                    type="checkbox"
                    class="toggle toggle-primary toggle-sm"
                  >
                </label>
              </div>
              <div
                v-if="gridResultData"
                class="grid grid-cols-2 gap-2"
              >
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Current Price
                  </div>
                  <div class="text-sm font-semibold text-primary">
                    ${{ formatGridPrice(gridStatsData?.current_price || gridResultData.reference_price) }}
                  </div>
                </div>
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Period High / Low
                  </div>
                  <div class="text-sm font-semibold">
                    <template v-if="gridStatsData">
                      ${{ formatGridPrice(gridStatsData.period_high) }} / ${{ formatGridPrice(gridStatsData.period_low) }}
                    </template>
                    <template v-else>
                      —
                    </template>
                  </div>
                </div>
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Grid Range
                  </div>
                  <div class="text-sm font-semibold">
                    <template v-if="gridStatsData">
                      ${{ formatGridPrice(gridStatsData.grid_range) }}
                    </template>
                    <template v-else>
                      —
                    </template>
                  </div>
                </div>
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Entry / Exit Spacing
                  </div>
                  <div class="text-sm font-semibold">
                    ${{ formatGridPrice(gridResultData.entry_spacing_dollars) }} / ${{ formatGridPrice(gridResultData.exit_spacing_dollars) }}
                  </div>
                </div>
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Order Size
                  </div>
                  <div class="text-sm font-semibold">
                    {{ gridResultData.order_size?.toFixed(6) }}
                  </div>
                  <div
                    v-if="gridResultData.reference_price"
                    class="text-xs text-base-content/50"
                  >
                    ${{ (gridResultData.order_size * gridResultData.reference_price).toFixed(2) }}
                  </div>
                </div>
                <div class="bg-base-200 rounded-lg p-2">
                  <div class="text-xs text-base-content/60">
                    Position Max Size
                  </div>
                  <div class="text-sm font-semibold">
                    {{ gridResultData.position_max_size?.toFixed(6) || '—' }}
                  </div>
                </div>
              </div>
              <!-- Validation Warning -->
              <div
                v-if="gridStatsData && gridStatsData.order_size_clamped"
                class="alert alert-warning text-xs py-2 mt-2"
              >
                <span>Order size was clamped to minimum ${{ gridStatsData.min_order_usd }} USD. Total capital (${{ gridStatsData.total_capital_usd?.toFixed(2) }}) exceeds input capital. Reduce levels or increase capital.</span>
              </div>
              <div
                v-else-if="!gridResultData"
                class="text-sm text-base-content/50 italic"
              >
                Waiting for grid calculation...
              </div>
            </div>

            <!-- Bot Tab (renamed from Overview) -->
            <div v-show="activeTab === 'Bot'">
              <div class="grid grid-cols-1 gap-4">
                <!-- Modified to use a 2-column grid for Name and Trading Pair -->
                <div class="grid grid-cols-2 gap-4">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Name</span>
                    </label>
                    <input
                      v-model="form.name"
                      type="text"
                      placeholder="Bot Name"
                      class="input input-bordered"
                      required
                    >
                  </div>

                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Trading Pair</span>
                    </label>
                    <!-- Symbol options selector (for cointegration pairs) -->
                    <select
                      v-if="prefilledData?.symbolOptions?.length"
                      v-model="form.symbol"
                      class="select select-bordered"
                      required
                      @change="onSymbolOptionChange"
                    >
                      <option
                        disabled
                        value=""
                      >
                        Select a symbol
                      </option>
                      <option
                        v-for="opt in prefilledData.symbolOptions"
                        :key="opt.symbol"
                        :value="opt.symbol"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <!-- Standard symbol dropdown -->
                    <select
                      v-else
                      v-model="symbolSelectValue"
                      class="select select-bordered"
                      required
                      :disabled="
                        !symbolPairs || Object.keys(symbolPairs).length === 0
                      "
                    >
                      <option
                        disabled
                        value=""
                      >
                        {{
                          !symbolPairs || Object.keys(symbolPairs).length === 0
                            ? "Loading pairs..."
                            : "Select a trading pair"
                        }}
                      </option>
                      <!-- Non-whitelisted symbol option (for prefilled or editing) -->
                      <option
                        v-if="!isSymbolInPairs && form.symbol"
                        :value="`${selectedCategory || 'Linear'}|${form.symbol}`"
                      >
                        {{ form.symbol }} ({{ selectedCategory || 'Linear' }})
                      </option>
                      <optgroup
                        v-for="(pairs, category) in symbolPairs"
                        :key="category"
                        :label="category"
                      >
                        <option
                          v-for="symbol in Object.keys(pairs)"
                          :key="`${category}-${symbol}`"
                          :value="`${category}|${symbol}`"
                        >
                          {{ symbol }}
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div
                  v-if="!isEditing"
                  class="form-control"
                >
                  <label class="label">
                    <span class="label-text">Account</span>
                  </label>
                  <select
                    v-model="form.account_id"
                    class="select select-bordered"
                    required
                    @change="handleAccountChange"
                  >
                    <option
                      disabled
                      value=""
                    >
                      {{
                        accounts.length === 0
                          ? "No accounts available"
                          : "Select an account"
                      }}
                    </option>
                    <option
                      v-for="account in accounts"
                      :key="account.id"
                      :value="account.id"
                    >
                      {{ account.name }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="!isEditing"
                  class="form-control"
                >
                  <label class="label">
                    <span class="label-text">API Key</span>
                  </label>
                  <div
                    ref="apiKeysDropdown"
                    class="dropdown dropdown-bottom dropdown-end w-full"
                    :class="{ 'dropdown-open': apiKeysDropdownOpen }"
                  >
                    <div
                      class="input input-bordered w-full flex items-center justify-between cursor-pointer pr-2"
                      @click="apiKeysDropdownOpen = !apiKeysDropdownOpen"
                    >
                      <span
                        v-if="form.api_key_id"
                        class="truncate flex-1"
                      >
                        {{ selectedApiKeyDescription }}
                      </span>
                      <span
                        v-else
                        class="text-base-content/50"
                      >
                        {{ isInitializing ? "Loading..." : (apiKeys.length === 0 ? "No API keys" : "Select an API key") }}
                      </span>
                      <svg
                        class="w-4 h-4 ml-auto flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    <div
                      v-show="apiKeysDropdownOpen"
                      class="dropdown-content z-[9999] p-2 shadow-lg bg-base-100 rounded-lg w-full max-h-60 overflow-y-auto mt-1"
                      style="position: absolute; left: 0; right: 0;"
                    >
                      <div
                        v-if="!isInitializing && apiKeys.length === 0"
                        class="text-sm text-base-content/70 p-2"
                      >
                        No API keys
                      </div>
                      <div v-else>
                        <!-- Available API keys (unused) -->
                        <template v-if="availableApiKeys.length > 0">
                          <div class="text-xs font-semibold text-base-content/70 mb-1 uppercase px-2">
                            Available
                          </div>
                          <label
                            v-for="apiKey in availableApiKeys"
                            :key="apiKey.id"
                            class="flex items-center gap-2 p-2 hover:bg-base-200 rounded cursor-pointer"
                          >
                            <input
                              v-model="form.api_key_id"
                              type="radio"
                              :value="apiKey.id"
                              class="radio radio-sm"
                              @click.stop
                              @change="handleApiKeySelection"
                            >
                            <span class="text-sm">{{ apiKey.description || apiKey.api_key }}</span>
                            <span
                              v-if="apiKey.exchange"
                              class="text-xs text-base-content/50 ml-auto"
                            >
                              {{ apiKey.exchange }}
                            </span>
                          </label>
                        </template>
                        <!-- In-use API keys -->
                        <template v-if="inUseApiKeys.length > 0">
                          <div
                            v-if="availableApiKeys.length > 0"
                            class="divider my-2"
                          />
                          <div class="text-xs font-semibold text-base-content/50 mb-1 uppercase px-2">
                            In Use
                          </div>
                          <label
                            v-for="apiKey in inUseApiKeys"
                            :key="apiKey.id"
                            class="flex items-center gap-2 p-2 hover:bg-base-200 rounded cursor-pointer opacity-60"
                          >
                            <input
                              v-model="form.api_key_id"
                              type="radio"
                              :value="apiKey.id"
                              class="radio radio-sm"
                              @click.stop
                              @change="handleApiKeySelection"
                            >
                            <span class="text-sm truncate">{{ apiKey.description || apiKey.api_key }}</span>
                            <span
                              v-if="apiKey.bots?.[0]?.name"
                              class="text-xs text-base-content/50 truncate"
                            >
                              ({{ apiKey.bots[0].name }})
                            </span>
                            <span
                              v-if="apiKey.exchange"
                              class="text-xs text-base-content/50 ml-auto flex-shrink-0"
                            >
                              {{ apiKey.exchange }}
                            </span>
                          </label>
                        </template>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="!isInitializing && apiKeys.length === 0 && !botToEdit"
                    class="text-sm text-error mt-2"
                  >
                    No API keys found. Please create an API key first.
                  </div>
                  <div
                    v-else-if="!isInitializing && availableApiKeys.length === 0 && !botToEdit"
                    class="text-sm text-warning mt-2"
                  >
                    All API keys are in use. You can still select one, or create a new API key.
                  </div>
                </div>

                <!-- Leverage slider - moved to Overview tab in mobile view -->
                <div
                  v-if="
                    form.account_id && (form.api_key_id && form.api_key_id !== '' || (form.api_key_ids && form.api_key_ids.length > 0))
                  "
                  class="form-control"
                >
                  <label class="label">
                    <span class="label-text">
                      Leverage
                      <div
                        class="tooltip tooltip-right"
                        data-tip="This setting will update the leverage for this bot"
                      >
                        <i
                          class="fas fa-info-circle text-xs ml-1 text-base-content/70"
                        />
                      </div>
                    </span>
                    <span class="label-text-alt">{{
                      typeof form.leverage === "number"
                        ? form.leverage.toFixed(1)
                        : form.leverage
                    }}x</span>
                  </label>
                  <input
                    v-model.number="form.leverage"
                    type="range"
                    min="1"
                    max="10"
                    class="range range-primary"
                    step="0.1"
                  >
                  <div class="w-full flex justify-between text-xs px-2 mt-1">
                    <span>1x</span>
                    <span>2x</span>
                    <span>3x</span>
                    <span>4x</span>
                    <span>5x</span>
                    <span>6x</span>
                    <span>7x</span>
                    <span>8x</span>
                    <span>9x</span>
                    <span>10x</span>
                  </div>
                  <div class="w-full flex justify-between text-xs px-2">
                    <template
                      v-for="i in 91"
                      :key="i"
                    >
                      <div
                        v-if="(i - 1) % 10 === 0"
                        class="h-1 w-0.5 bg-base-content/30"
                      />
                    </template>
                  </div>
                </div>

                <!-- Add wallet balance and open orders info -->
                <div
                  class="grid grid-cols-2 gap-4 text-sm text-base-content/70 mt-2"
                >
                  <div
                    v-if="openOrdersCount === null && form.api_key_id"
                  >
                    Fetching data...
                  </div>
                  <div
                    v-else-if="
                      openOrdersCount !== '?' && form.api_key_id
                    "
                  >
                    Open Orders: {{ openOrdersCount }}
                  </div>
                  <div v-if="walletBalance !== null">
                    Wallet Balance:
                    {{ relevantCurrency === "BTC" ? "₿" : "$"
                    }}{{
                      walletBalance.toLocaleString(undefined, {
                        minimumFractionDigits: relevantCurrency === "BTC" ? 8 : 2,
                        maximumFractionDigits: relevantCurrency === "BTC" ? 8 : 2,
                      })
                    }}
                  </div>
                </div>

                <!-- Bot Summary (moved from Summary tab) -->
                <div
                  v-if="currentStrategy && form.strategy_id && form.symbol"
                  class="mt-4"
                >
                  <BotSummary
                    :strategy="currentStrategy"
                    :config="form.strategy_config"
                    :special-field-values="specialFieldValues"
                    :symbol="form.symbol"
                  />
                </div>
              </div>
            </div>

            <!-- Strategy Tab -->
            <div v-show="activeTab === 'Strategy'">
              <div class="form-control mb-4">
                <label class="label">
                  <span class="label-text">Strategy</span>
                </label>
                <select
                  v-model="form.strategy_id"
                  class="select select-bordered w-full"
                  required
                  @change="loadStrategyFields"
                >
                  <option
                    disabled
                    value=""
                  >
                    Select a strategy
                  </option>
                  <option
                    v-for="strategy in strategies"
                    :key="strategy.id"
                    :value="strategy.id"
                  >
                    {{ strategy.name }}
                  </option>
                </select>
                <div
                  v-if="strategyError"
                  class="text-sm text-error mt-2"
                >
                  {{ strategyError }}
                </div>
              </div>

              <!-- Visible Strategy Fields -->
              <div
                v-if="visibleStrategyFields.length > 0"
                class="form-control"
              >
                <div class="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div
                    v-for="field in visibleStrategyFields"
                    :key="field.name"
                  >
                    <label class="label">
                      <span
                        class="text-xs"
                        :class="isGridOverrideField(field.name) ? 'text-primary font-semibold' : 'text-gray-500'"
                      >
                        {{ humanizeFieldName(field.name) }}
                        <span
                          v-if="isGridOverrideField(field.name)"
                          class="badge badge-xs badge-primary ml-1"
                        >grid</span>
                      </span>
                    </label>
                    <div class="relative">
                      <template v-if="getInputType(field).type === 'dropdown'">
                        <select
                          v-model="form.strategy_config[field.name]"
                          class="select select-bordered w-full"
                          :class="[
                            getInputType(field).class,
                            { 'opacity-50': loadingSmartFields[field.name] },
                            { 'select-primary border-primary/30': isGridOverrideField(field.name) },
                          ]"
                        >
                          <!-- For regular dropdowns -->
                          <template v-if="field.type !== 'boolean'">
                            <option value="">
                              Select {{ humanizeFieldName(field.name) }}
                            </option>
                            <option
                              v-for="option in getInputType(field).options"
                              :key="option"
                              :value="option"
                            >
                              {{ humanizeFieldName(option) }}
                            </option>
                          </template>
                          <!-- For boolean type dropdowns -->
                          <template v-else>
                            <option value="True">
                              True
                            </option>
                            <option value="False">
                              False
                            </option>
                          </template>
                        </select>
                      </template>
                      <div
                        v-else-if="field.type === 'quantity'"
                        class="relative"
                      >
                        <div class="input input-bordered flex items-center pr-12">
                          <input
                            v-model="form.strategy_config[field.name]"
                            :type="shouldUseNumberInput(field) ? 'number' : 'text'"
                            class="grow border-none focus:outline-none text-sm bg-transparent no-scrollbar"
                            step="any"
                            :class="[
                              { 'opacity-50': loadingSmartFields[field.name] },
                              { 'text-error': isQuantityFieldInvalid(field.name) },
                            ]"
                            :placeholder="field.name"
                            @input="validateField(field.name)"
                            @wheel.prevent
                          >
                          <div class="absolute right-3">
                            <span class="badge badge-neutral">USD</span>
                          </div>
                        </div>
                        <div
                          v-if="isQuantityFieldInvalid(field.name)"
                          class="text-error text-sm mt-1"
                        >
                          Minimum quantity is ${{ getMinOrderValueForDisplay }}
                        </div>
                      </div>
                      <input
                        v-else
                        v-model="form.strategy_config[field.name]"
                        :type="shouldUseNumberInput(field) ? 'number' : 'text'"
                        :readonly="getInputType(field).readonly"
                        :class="[
                          'input input-bordered w-full text-sm',
                          getInputType(field).class,
                          { 'opacity-50': loadingSmartFields[field.name] },
                          { 'input-error': isQuantityFieldInvalid(field.name) },
                          { 'input-primary border-primary/30': isGridOverrideField(field.name) },
                        ]"
                        :placeholder="field.name"
                        step="any"
                        @input="validateField(field.name)"
                        @wheel.prevent
                      >
                      <!-- Loading spinner -->
                      <div
                        v-if="loadingSmartFields[field.name]"
                        class="absolute right-2 top-1/2 transform -translate-y-1/2"
                      >
                        <span class="loading loading-spinner loading-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hidden/Advanced Fields Collapsible Section -->
              <div
                v-if="hiddenStrategyFields.length > 0"
                class="form-control mt-4"
              >
                <div class="collapse collapse-arrow bg-base-200 rounded-lg">
                  <input
                    v-model="hiddenFieldsExpanded"
                    type="checkbox"
                    class="peer"
                  >
                  <div class="collapse-title text-sm font-medium flex items-center gap-2 py-2 min-h-0">
                    <span>Advanced Fields</span>
                    <span class="badge badge-sm badge-neutral">{{ hiddenStrategyFields.length }}</span>
                  </div>
                  <div class="collapse-content">
                    <div class="grid grid-cols-2 gap-x-4 gap-y-2 pt-2">
                      <div
                        v-for="field in hiddenStrategyFields"
                        :key="field.name"
                      >
                        <label class="label">
                          <span
                            class="text-xs"
                            :class="isGridOverrideField(field.name) ? 'text-primary font-semibold' : 'text-gray-500'"
                          >
                            {{ humanizeFieldName(field.name) }}
                            <span
                              v-if="isGridOverrideField(field.name)"
                              class="badge badge-xs badge-primary ml-1"
                            >grid</span>
                          </span>
                        </label>
                        <div class="relative">
                          <template v-if="getInputType(field).type === 'dropdown'">
                            <select
                              v-model="form.strategy_config[field.name]"
                              class="select select-bordered w-full"
                              :class="[
                                getInputType(field).class,
                                { 'opacity-50': loadingSmartFields[field.name] },
                                { 'select-primary border-primary/30': isGridOverrideField(field.name) },
                              ]"
                            >
                              <!-- For regular dropdowns -->
                              <template v-if="field.type !== 'boolean'">
                                <option value="">
                                  Select {{ humanizeFieldName(field.name) }}
                                </option>
                                <option
                                  v-for="option in getInputType(field).options"
                                  :key="option"
                                  :value="option"
                                >
                                  {{ humanizeFieldName(option) }}
                                </option>
                              </template>
                              <!-- For boolean type dropdowns -->
                              <template v-else>
                                <option value="True">
                                  True
                                </option>
                                <option value="False">
                                  False
                                </option>
                              </template>
                            </select>
                          </template>
                          <div
                            v-else-if="field.type === 'quantity'"
                            class="relative"
                          >
                            <div class="input input-bordered flex items-center pr-12">
                              <input
                                v-model="form.strategy_config[field.name]"
                                :type="shouldUseNumberInput(field) ? 'number' : 'text'"
                                class="grow border-none focus:outline-none text-sm bg-transparent no-scrollbar"
                                step="any"
                                :class="[
                                  { 'opacity-50': loadingSmartFields[field.name] },
                                  { 'text-error': isQuantityFieldInvalid(field.name) },
                                ]"
                                :placeholder="field.name"
                                @input="validateField(field.name)"
                                @wheel.prevent
                              >
                              <div class="absolute right-3">
                                <span class="badge badge-neutral">USD</span>
                              </div>
                            </div>
                            <div
                              v-if="isQuantityFieldInvalid(field.name)"
                              class="text-error text-sm mt-1"
                            >
                              Minimum quantity is ${{ getMinOrderValueForDisplay }}
                            </div>
                          </div>
                          <input
                            v-else
                            v-model="form.strategy_config[field.name]"
                            :type="shouldUseNumberInput(field) ? 'number' : 'text'"
                            :readonly="getInputType(field).readonly"
                            :class="[
                              'input input-bordered w-full text-sm',
                              getInputType(field).class,
                              { 'opacity-50': loadingSmartFields[field.name] },
                              { 'input-error': isQuantityFieldInvalid(field.name) },
                              { 'input-primary border-primary/30': isGridOverrideField(field.name) },
                            ]"
                            :placeholder="field.name"
                            step="any"
                            @input="validateField(field.name)"
                            @wheel.prevent
                          >
                          <!-- Loading spinner -->
                          <div
                            v-if="loadingSmartFields[field.name]"
                            class="absolute right-2 top-1/2 transform -translate-y-1/2"
                          >
                            <span class="loading loading-spinner loading-sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Advanced Tab -->
            <div v-show="activeTab === 'Advanced'">
              <div class="form-control mb-4">
                <label class="label">
                  <span class="label-text">
                    Log Level
                    <div
                      class="tooltip tooltip-right"
                      data-tip="Set the logging verbosity for this bot. WARNING is recommended for most bots to reduce logging volume"
                    >
                      <i
                        class="fas fa-info-circle text-xs ml-1 text-base-content/70"
                      />
                    </div>
                  </span>
                </label>
                <select
                  v-model="form.log_level"
                  class="select select-bordered w-full"
                >
                  <option value="DEBUG">
                    DEBUG - Most detailed logging
                  </option>
                  <option value="INFO">
                    INFO - General information
                  </option>
                  <option value="WARNING">
                    WARNING - Important notices (Recommended)
                  </option>
                  <option value="ERROR">
                    ERROR - Only errors
                  </option>
                </select>
                <div class="text-sm text-base-content/60 mt-2">
                  <ul class="space-y-1">
                    <li>• <strong>DEBUG:</strong> Very detailed logging, useful for troubleshooting</li>
                    <li>• <strong>INFO:</strong> Standard logging for monitoring bot activity</li>
                    <li>• <strong>WARNING:</strong> Only important events and warnings</li>
                    <li>• <strong>ERROR:</strong> Only log errors and critical issues</li>
                  </ul>
                </div>
              </div>
          
              <div class="form-control">
                <label class="label">
                  <span class="label-text">
                    Node Type Preference
                    <div
                      class="tooltip tooltip-right"
                      data-tip="Choose whether to run this bot on AWS Spot instances (cheaper but may be interrupted) or On-Demand instances (more reliable but more expensive)"
                    >
                      <i
                        class="fas fa-info-circle text-xs ml-1 text-base-content/70"
                      />
                    </div>
                  </span>
                </label>
                <select
                  v-model="form.node_type_preference"
                  class="select select-bordered w-full"
                >
                  <option value="spot">
                    Spot Instance (Cost-effective)
                  </option>
                  <option value="on_demand">
                    On-Demand Instance (Reliable)
                  </option>
                </select>
                <div class="text-sm text-base-content/60 mt-2">
                  <ul class="space-y-1">
                    <li>• <strong>Spot Instances:</strong> Up to 90% cheaper but may be interrupted with 2-minute notice</li>
                    <li>• <strong>On-Demand Instances:</strong> Always available but at regular pricing</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Error message for no API keys -->
            <div
              v-if="!isInitializing && !isEditing && apiKeys.length === 0 && form.account_id"
              class="text-sm text-error mt-4"
            >
              No API keys found for this account. Please create an API key first.
            </div>

            <!-- Message to select an account first -->
            <div
              v-if="!isEditing && !form.account_id"
              class="text-sm mt-4 text-neutral-content"
            >
              Please select an account to view available API keys.
            </div>

            <!-- Hidden file input for import -->
            <input
              ref="importFileInput"
              type="file"
              accept=".json"
              class="hidden"
              @change="handleImportFile"
            >

            <div class="modal-action">
              <!-- EXPORT BUTTON - Only when editing (leftmost) -->
              <button
                v-if="isEditing"
                type="button"
                class="btn btn-outline mr-auto"
                :disabled="isSubmitting"
                @click="exportConfig"
              >
                <i class="fas fa-file-export mr-2" />
                Export
              </button>

              <div
                v-if="!isEditing"
                class="flex flex-col items-start gap-2 mr-auto"
              >
                <label class="label cursor-pointer justify-start gap-2">
                  <input
                    v-model="form.auto_start"
                    type="checkbox"
                    class="checkbox checkbox-primary checkbox-sm"
                  >
                  <span class="label-text">Start bot automatically</span>
                </label>

                <!-- IMPORT BUTTON - Only when creating (below checkbox) -->
                <button
                  type="button"
                  :disabled="isImporting"
                  class="btn btn-outline btn-sm"
                  @click="triggerImport"
                >
                  <i
                    v-if="!isImporting"
                    class="fas fa-file-import mr-2"
                  />
                  <i
                    v-else
                    class="fas fa-spinner fa-spin mr-2"
                  />
                  {{ isImporting ? 'Importing...' : 'Import' }}
                </button>
              </div>

              <!-- Import error display -->
              <div
                v-if="importError"
                class="text-sm text-error mt-2"
              >
                <i class="fas fa-exclamation-circle mr-1" />
                {{ importError }}
              </div>

              <button
                type="button"
                class="btn"
                @click="$emit('close')"
              >
                Cancel
              </button>

              <button
                v-if="isEditing"
                type="button"
                class="btn btn-primary"
                :disabled="isSubmitting"
                @click="submitFormKeepOpen"
              >
                {{ isSubmitting ? "Updating..." : "Update" }}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="isSubmitting"
                @click="submitForm"
              >
                {{
                  isSubmitting
                    ? isEditing
                      ? "Updating..."
                      : "Creating..."
                    : isEditing
                      ? "Update & Close"
                      : "Create"
                }}
              </button>
            </div>
          </form>
        </div><!-- close right/single panel -->
      </div><!-- close main content flex container -->
    </div><!-- close modal-box -->
  </div><!-- close modal -->
</template>

<script>
import api from "@/api/axios";
import {
  getSpecialFieldValue,
  getSpecialFields,
} from "@/services/specialFieldService";
import {
  getDefaultFields,
  getDefaultFieldValue,
} from "@/services/smartDefaultService";
import walletService from "@/services/walletService";
import BotSummary from "@/components/BotSummary.vue";
import RangeExplorerPreview from "@/components/RangeExplorerPreview.vue";
import instrumentInfoService from "@/services/instrumentInfoService";
import { defaultCurrencyRounding } from "@/utils/formatting";
import { useStore } from "vuex";
import { computed } from "vue";
import { toBooleanValue, isTrueValue } from "@/services/typeService";

export default {
  components: {
    BotSummary,
    RangeExplorerPreview,
  },
  props: {
    show: Boolean,
    botToEdit: {
      type: Object,
      default: () => null,
    },
    accountId: {
      type: [String, Number],
      default: null,
    },
    prefilledData: {
      type: Object,
      default: () => null,
    },
  },
  emits: ["close", "submit", "notification"],
  setup() {
    const store = useStore();
    const symbolPairs = computed(() => store.getters["symbols/getSymbolPairs"]);

    const getSymbolInfo = (symbol) => {
      return store.getters["symbols/getSymbolInfo"](symbol);
    };

    const fetchSymbolPairs = async () => {
      try {
        if (!store.state.symbols.isInitialized) {
          await store.dispatch("symbols/initializeSymbols");
        }
      } catch (error) {
        console.error("Error fetching symbol pairs:", error);
      }
    };

    return {
      symbolPairs,
      getSymbolInfo,
      fetchSymbolPairs,
      defaultCurrencyRounding,
    };
  },
  data() {
    return {
      isSubmitting: false,
      isInitializing: true,  // Start with loading state true
      updateTimeout: null,
      resizeTimeout: null,
      strategyError: "",
      importedConfig: null,
      isImporting: false,
      importError: null,
      form: {
        name: "",
        api_key_id: "",
        api_key_ids: [],
        symbol: "",
        strategy_id: "",
        strategy_config: {},
        account_name: "",
        api_key_description: "",
        auto_start: false,
        account_id: "",
        leverage: 1,
        node_type_preference: "spot",
        log_level: "WARNING",
      },
      selectedCategory: null,
      apiKeys: [],
      accounts: [],
      strategies: [],
      strategyFields: [],
      highestGridPrice: null,
      specialFieldValues: {},
      specialFields: [],
      activeTab: "Bot",
      tabRefs: [],
      openOrdersCount: null,
      walletBalance: null,
      previousConfig: null,
      defaultFields: [],
      loadingSmartFields: {},
      minOrderValue: null,
      invalidFields: new Set(),
      globalSettings: [],
      isLoadingSymbols: false,
      isLoading: {
        symbols: false,
        wallet: false,
        orders: false,
        strategies: false,
      },
      apiKeysDropdownOpen: false,
      showGridChartMobile: false,
      gridResultData: null,
      gridStatsData: null,
      gridCoupled: true,
      requestCache: new Map(),
      requestInProgress: new Map(),
      useMobileView: true,
      hiddenFieldsExpanded: false,
    };
  },
  computed: {
    isEditing() {
      return !!this.botToEdit && !this.botToEdit.isNewBot;
    },
    currentStrategy() {
      return this.strategies.find((s) => s.id === this.form.strategy_id);
    },
    isGridStrategy() {
      if (!this.currentStrategy) return false;
      const name = this.currentStrategy.name.toLowerCase();
      return name.includes('grid-v3') || name.includes('v2-grid');
    },
    isGridMode() {
      return this.isGridStrategy && !!this.form.symbol;
    },
    tabs() {
      if (this.isGridMode) return ['Grid', 'Bot', 'Strategy', 'Advanced'];
      return ['Bot', 'Strategy', 'Advanced'];
    },
    gridTradingMode() {
      const mode = this.form.strategy_config?.trading_mode;
      if (mode) return mode.toUpperCase();
      return 'SHORT';
    },
    gridDefaults() {
      return this.form.strategy_config?._range_explorer || null;
    },
    visibleStrategyFields() {
      // Fields where hidden is not explicitly true (fail visible)
      return this.strategyFields.filter((f) => f.hidden !== true);
    },
    hiddenStrategyFields() {
      // Only fields where hidden is explicitly true
      return this.strategyFields.filter((f) => f.hidden === true);
    },
    relevantCurrency() {
      if (!this.form.symbol) return "";
      const settlementCurrency = this.getSettlementCurrencyFromSymbol(this.form.symbol);
      return settlementCurrency || walletService.getRelevantCurrency(this.form.symbol);
    },
    isFormValid() {
      return this.invalidFields.size === 0;
    },
    selectedApiKeyDescriptions() {
      // Get descriptions of selected API keys
      if (!this.form.api_key_ids || this.form.api_key_ids.length === 0) {
        return [];
      }
      return this.form.api_key_ids.map(id => {
        const apiKey = this.apiKeys.find(key => key.id === id);
        return apiKey ? (apiKey.description || apiKey.api_key || `Key ${id}`) : `Key ${id}`;
      });
    },
    selectedApiKeyDescription() {
      // Get description of the single selected API key
      if (!this.form.api_key_id) {
        return '';
      }
      const apiKey = this.apiKeys.find(key => key.id === this.form.api_key_id);
      return apiKey ? (apiKey.description || apiKey.api_key || `Key ${this.form.api_key_id}`) : `Key ${this.form.api_key_id}`;
    },
    availableApiKeys() {
      // Filter out API keys that have an active bot
      return this.apiKeys.filter((key) => {
        if (key.active_bot) {
          // If there's an active bot already using this key, it's not available
          return false;
        }

        // Check if there are any bots with status active
        const hasActiveBots = key.bots?.some((bot) =>
          bot.status === "active"
        );

        return !hasActiveBots;
      });
    },
    inUseApiKeys() {
      // Filter API keys that have an active bot (opposite of availableApiKeys)
      return this.apiKeys.filter((key) => {
        if (key.active_bot) {
          return true;
        }
        const hasActiveBots = key.bots?.some((bot) =>
          bot.status === "active"
        );
        return hasActiveBots;
      });
    },
    groupedApiKeys() {
      // Group available API keys by exchange
      const grouped = {};
      this.availableApiKeys.forEach(key => {
        const exchange = key.exchange || 'Unknown';
        if (!grouped[exchange]) {
          grouped[exchange] = [];
        }
        grouped[exchange].push(key);
      });
      return grouped;
    },
    isFormDisabled() {
      return !this.isEditing && this.apiKeys.length === 0;
    },
    isInverseBot() {
      return !this.form.symbol?.includes("USDT");
    },
    getMinOrderValueForDisplay() {
      const category = this.getCategory();
      if (category === 'Inverse') {
        return 5;
      }
      // Spot & Linear get $6 minimum
      return 6;
    },
    isSymbolInPairs() {
      // Check if symbol is in whitelist (for prefilled or current form)
      const symbol = this.prefilledData?.symbol || this.form.symbol;
      const category = this.prefilledData?.category || this.selectedCategory;
      if (!symbol) return true;

      // Check all categories if no specific category
      if (!category) {
        for (const cat of Object.keys(this.symbolPairs || {})) {
          if (this.symbolPairs[cat]?.[symbol]) return true;
        }
        return false;
      }
      return this.symbolPairs?.[category]?.[symbol] !== undefined;
    },
    symbolSelectValue: {
      get() {
        // When editing, construct the composite value from form.symbol and selectedCategory
        if (this.form.symbol && this.selectedCategory) {
          const value = `${this.selectedCategory}|${this.form.symbol}`;
          return value;
        }
        return this.form.symbol || "";
      },
      set(value) {
        // Handle composite value selection
        if (value && value.includes('|')) {
          const [category, symbol] = value.split('|');
          this.selectedCategory = category;
          this.form.symbol = symbol;
        } else {
          this.form.symbol = value;
        }
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Set loading state immediately when modal opens
        this.isInitializing = true;
        this.activeTab = this.botToEdit ? "Strategy" : "Bot";
        this.fetchSymbolPairs();
        this.initializeForm();
        if (this.botToEdit) {
          this.fetchOpenOrdersCount();
        }
        // Check modal size after it's visible and content is loaded
        this.$nextTick(() => {
          this.checkModalSize();
          // After initialization, switch to Grid tab if grid mode is active
          if (this.isGridMode) {
            this.activeTab = 'Grid';
          }
        });
      }
    },
    isGridMode(newVal, oldVal) {
      if (oldVal && !newVal) {
        // Switching away from grid strategy — leave Grid tab
        if (this.activeTab === 'Grid') {
          this.activeTab = 'Bot';
        }
      }
      if (!oldVal && newVal) {
        // Switching to grid strategy — auto-select Grid tab
        this.activeTab = 'Grid';
      }
    },
    gridCoupled(newVal) {
      if (newVal && this.gridResultData) {
        // Re-coupled: push latest Range Explorer values back into strategy config
        this.handleRangeExplorerConfig(this.gridResultData);
      }
      // Persist the toggle state
      this.form.strategy_config._grid_coupled = newVal;
    },
    botToEdit(newVal) {
      if (newVal) {
        // Set loading state immediately when bot data changes
        this.isInitializing = true;
        this.initializeForm();
        this.$nextTick(() => {
          this.checkModalSize();
        });
      }
    },
    "form.strategy_id": {
      async handler() {
        await this.loadStrategyFields();
        this.updateSpecialFieldValues();
        this.$nextTick(() => {
          this.checkModalSize();
        });
      },
    },
    strategyFields: {
      handler() {
        this.$nextTick(() => {
          this.checkModalSize();
        });
      },
      deep: true,
    },
    activeTab() {},
    "form.symbol": {
      async handler() {
        this.updateSpecialFieldValues();
        if (this.form.symbol && (this.form.api_key_id || (this.form.api_key_ids && this.form.api_key_ids.length > 0))) {
          this.fetchWalletBalance();
          this.updateSmartFields();
          this.fetchOpenOrdersCount();
          this.fetchMinOrderValue();
        } else {
          this.walletBalance = null;
          this.openOrdersCount = null;
          this.minOrderValue = null;
        }
      },
    },
    "form.api_key_id"(newVal) {
      if (newVal) {
        // Prefetch wallet data if symbol is already selected
        if (this.form.symbol) {
          this.fetchWalletBalance();
          this.fetchOpenOrdersCount();
        }

        // Update leverage from the selected API key
        if (!this.isEditing) {
          const apiKey = this.apiKeys.find((key) => key.id === newVal);
          if (apiKey) {
            // Only update leverage if this is truly a new bot, not a copied or imported bot
            const isCopiedBot = this.botToEdit && this.botToEdit.isNewBot === true;
            const isImportedBot = this.importedConfig !== null;
            if (!isCopiedBot && !isImportedBot) {
              this.form.leverage = apiKey.leverage || 1;
            }
          }
        }
      }
    },
    "form.api_key_ids": {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          // Always update the primary API key to the first selected one
          this.form.api_key_id = newVal[0];
          
          // Prefetch wallet data if symbol is already selected
          if (this.form.symbol) {
            this.fetchWalletBalance();
            this.fetchOpenOrdersCount();
          }

          // Update leverage from the first selected API key
          if (!this.isEditing && newVal.length > 0) {
            const apiKey = this.apiKeys.find((key) => key.id === newVal[0]);
            if (apiKey) {
              // Only update leverage if this is truly a new bot, not a copied or imported bot
              const isCopiedBot = this.botToEdit && this.botToEdit.isNewBot === true;
              const isImportedBot = this.importedConfig !== null;
              if (!isCopiedBot && !isImportedBot) {
                this.form.leverage = apiKey.leverage || 1;
              }
            }
          }
        } else {
          // Clear primary API key if no API keys are selected
          this.form.api_key_id = "";
        }
      },
      deep: true
    },
    "form.strategy_config.n_levels": {
      handler() {
        this.updateSmartFields();
      },
      deep: true,
      immediate: true,
    },
    "form.strategy_config": {
      deep: true,
      handler() {
        this.updateSmartFields();
        this.$nextTick(() => {
          this.checkModalSize();
        });
      },
    },
    selectedCategory: {
      handler(newVal) {
        // Force the select to update by triggering reactivity
        if (this.form.symbol && newVal) {
          this.$nextTick(() => {
            // Force re-evaluation of the computed property
            if (this.$el?.querySelector) {
              this.$el.querySelector('select[class*="select-bordered"]');
            }
          });
        }
      },
      immediate: true
    },
    availableApiKeys: {
      handler(newAvailableKeys) {
        // Only clean up API keys when not editing (when editing, keep existing selections)
        if (!this.isEditing) {
          // Clean up selected API keys that are no longer available
          if (this.form.api_key_ids && this.form.api_key_ids.length > 0) {
            const availableIds = newAvailableKeys.map(key => key.id);
            const filteredIds = this.form.api_key_ids.filter(id => availableIds.includes(id));
            
            // Only update if the selection has changed
            if (filteredIds.length !== this.form.api_key_ids.length) {
              this.form.api_key_ids = filteredIds;
              
              // If no API keys remain selected, clear the primary API key
              if (filteredIds.length === 0) {
                this.form.api_key_id = "";
              }
            }
          }
          
          // Also check if the primary API key is still available
          if (this.form.api_key_id && !newAvailableKeys.some(key => key.id === this.form.api_key_id)) {
            this.form.api_key_id = "";
          }
        }
      },
      deep: true
    },
  },
  mounted() {
    // Don't call initializeForm() here - it's called by the show watcher
    // This prevents fetching strategies/accounts when modal isn't visible
    this.defaultFields = getDefaultFields();
    this.checkModalSize();
    window.addEventListener("resize", this.checkModalSize);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkModalSize);
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    checkModalSize() {
      // Always use the tab system
      this.useMobileView = true;
    },

    onSymbolOptionChange() {
      this.selectedCategory = this.prefilledData?.category || 'Linear';
      // Update bot name based on selected symbol
      const baseSymbol = this.form.symbol.replace(/-USDT$/i, '');
      const tradingMode = this.prefilledData?.tradingMode || 'long';
      this.form.name = `${baseSymbol} USDT ${tradingMode === 'long' ? 'Long' : 'Short'} Coint`;
    },

    handleClickOutside(event) {
      const dropdown = this.$refs.apiKeysDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.apiKeysDropdownOpen = false;
      }
    },
    
    handleApiKeySelection() {
      // Sync api_key_ids with the selected api_key_id for consistency
      // This ensures the backend receives the correct API key in both fields
      if (this.form.api_key_id) {
        this.form.api_key_ids = [this.form.api_key_id];
      } else {
        this.form.api_key_ids = [];
      }
      // Close dropdown after the selection change has been processed
      this.$nextTick(() => {
        this.apiKeysDropdownOpen = false;
      });
    },
    
    getApiKeyDescriptions() {
      if (!this.botToEdit || !this.botToEdit.api_keys) {
        return 'No API keys';
      }

      // When editing, we can use the api_key_description from the bot directly
      // This avoids needing to fetch the full API keys list
      if (this.botToEdit.api_key_description) {
        // If there's only one API key, return its description
        if (this.botToEdit.api_keys && this.botToEdit.api_keys.length === 1) {
          return this.botToEdit.api_key_description;
        }
        // If there are multiple, indicate count and primary key description
        if (this.botToEdit.api_keys && this.botToEdit.api_keys.length > 1) {
          return `${this.botToEdit.api_keys.length} API keys (primary: ${this.botToEdit.api_key_description})`;
        }
      }

      // Fallback to showing count if no description available
      const keyCount = this.botToEdit.api_keys ? this.botToEdit.api_keys.length : 0;
      if (keyCount === 0) {
        return 'No API keys';
      } else if (keyCount === 1) {
        return this.botToEdit.api_key_description || 'API Key';
      } else {
        return `${keyCount} API keys`;
      }
    },

    shouldUseNumberInput(field) {
      // Use text input for fields that contain comma-separated values or are explicitly strings
      if (field.type === "string" || field.type === "text") {
        return false;
      }
      
      return ["int", "float", "decimal", "number", "quantity"].includes(field.type);
    },

    async initializeForm() {
      // Set loading state
      this.isInitializing = true;
      
      // Reset form state first
      this.resetState();

      // Store bot data for later use
      const botData = this.botToEdit;
      const accountIdToUse = this.botToEdit?.account_id || this.accountId || "";

      try {
        // First, load the critical data needed for dropdowns
        const criticalPromises = [
          this.fetchSymbolPairs(),
          this.fetchStrategies(),
          this.fetchSpecialFields(),
          this.fetchAccounts(),
        ];

        await Promise.all(criticalPromises).catch(console.error);

        // Now populate form AFTER accounts are loaded so dropdowns work correctly
        if (botData) {
          // Check if this is a copied bot (isNewBot flag is set)
          const isCopiedBot = botData.isNewBot === true;

          this.form = {
            name: botData.name,
            // When copying, preserve the values passed from parent component
            api_key_id: botData.api_key_id || "",
            api_key_ids: botData.api_key_ids || (botData.api_keys || []),
            symbol: botData.symbol,
            strategy_id: botData.strategy_id,
            strategy_config: { ...botData.strategy_config }, // Ensure we make a deep copy
            account_name: botData.account_name || "",
            api_key_description: botData.api_key_description || "",
            auto_start: isCopiedBot ? false : botData.auto_start,
            account_id: botData.account_id || "",
            leverage: botData.leverage || 1, // Preserve leverage for both edit and copy
            node_type_preference: botData.node_type_preference || "spot",
            log_level: botData.log_level || "WARNING",
          };

          // Store the original config for reference
          this.previousConfig = { ...botData.strategy_config };

          // Restore grid coupled toggle state
          if (botData.strategy_config?._grid_coupled !== undefined) {
            this.gridCoupled = botData.strategy_config._grid_coupled;
          }
        } else if (accountIdToUse) {
          // If creating a new bot with a specific account ID
          this.form.account_id = accountIdToUse;
        }

        // Apply prefilled data if provided (e.g., from Short Basket Signals)
        if (this.prefilledData) {
          if (this.prefilledData.name) {
            this.form.name = this.prefilledData.name;
          }
          if (this.prefilledData.symbol) {
            this.form.symbol = this.prefilledData.symbol;
          }
          if (this.prefilledData.category) {
            this.selectedCategory = this.prefilledData.category;
          }
          // Apply prefilled strategy config FIRST (e.g., trading_mode: 'short')
          // This must happen before setting strategy_id which triggers loadStrategyFields
          if (this.prefilledData.strategyConfig) {
            this.form.strategy_config = {
              ...this.form.strategy_config,
              ...this.prefilledData.strategyConfig
            };
          }
          // Auto-select strategy by name (try each in order until one matches)
          if (this.prefilledData.strategyNames) {
            for (const strategyName of this.prefilledData.strategyNames) {
              const strategy = this.strategies.find(s =>
                s.name.toLowerCase() === strategyName.toLowerCase()
              );
              if (strategy) {
                this.form.strategy_id = strategy.id;
                break;
              }
            }
          } else if (this.prefilledData.strategyName) {
            // Fallback for single strategyName (backwards compatibility)
            const strategy = this.strategies.find(s =>
              s.name.toLowerCase().includes(this.prefilledData.strategyName.toLowerCase())
            );
            if (strategy) {
              this.form.strategy_id = strategy.id;
            }
          }
        }

        // NOW set the selectedCategory after symbol pairs are loaded
        if (this.botToEdit) {
          // Set selectedCategory based on the bot's category
          this.selectedCategory = this.botToEdit.category || this.getCategoryFromSymbol(this.botToEdit.symbol);
        }

        // CRITICAL: Fetch API keys BEFORE showing the form
        // This prevents the "no API keys" error from flashing
        if (this.form.account_id) {
          await this.fetchApiKeys();

        // Ensure Vue updates the DOM before we try to set values
        await this.$nextTick();

        // When editing, leverage comes from the bot itself, not the API key
        // The bot stores its own leverage value
        if (this.isEditing) {
          this.form.leverage = this.botToEdit.leverage || 1;
        }
      }

      // Load strategy fields after we have the strategy data
      if (this.form.strategy_id) {
        await this.loadStrategyFields();
      }

      // Non-critical operations - can load in background
      if (this.botToEdit && !this.botToEdit.isNewBot) {
        // Start these in parallel but don't await them
        if (this.form.account_id && this.form.api_key_id) {
          this.fetchOpenOrdersCount();
          this.fetchWalletBalance();
        }
        this.fetchHighestGridLevel();
        this.updateSpecialFieldValues();
      }
      } finally {
        // Clear loading state
        this.isInitializing = false;
      }
    },

    async fetchAccounts() {
      try {
        const cacheKey = "accounts";
        const response = await this.cachedRequest(cacheKey, () =>
          api.get("/api/accounts/")
        );
        this.accounts = response.data;

        // If editing, set the form leverage from the bot
        if (this.isEditing) {
          this.form.leverage = this.botToEdit.leverage || 1;
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
        this.accounts = [];
      }
    },

    handleAccountChange() {
      // Reset API key selection when account changes
      this.form.api_key_id = "";
      this.walletBalance = null;
      this.openOrdersCount = null;

      // Fetch API keys for the selected account
      if (this.form.account_id) {
        this.fetchApiKeys();
      } else {
        this.apiKeys = [];
      }
    },

    handleRangeExplorerConfig(gridConfig) {
      if (!gridConfig) return;
      if (!this.gridCoupled) return;
      // Merge range explorer config into strategy_config
      // Range explorer keys take priority over manually entered values
      const isLong = this.gridTradingMode === 'LONG';
      const configKeys = [
        'calculator',
        'entry_spacing_dollars',
        'exit_spacing_dollars',
        'order_size',
        'position_max_size',
        'reference_price',
        'trading_mode',
        'order_max_per_side',
        'linear_spacing_factor',
        'max_order_size',
        'geometric_spacing_factor',
        'geometric_scaling_curve',
        // LONG: explorer sets floor, user controls ceiling
        // SHORT: explorer sets ceiling, user controls floor
        ...(isLong ? ['range_floor_price'] : ['range_ceiling_price']),
      ];
      for (const key of configKeys) {
        if (gridConfig[key] !== undefined) {
          let value = gridConfig[key];
          if (key === 'trading_mode' && typeof value === 'string') {
            value = value.toLowerCase();
          }
          this.form.strategy_config[key] = value;
        }
      }
    },

    formatGridPrice(val) {
      if (val == null) return '—';
      const num = Number(val);
      if (isNaN(num)) return '—';
      if (num >= 1) return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      if (num >= 0.01) return num.toFixed(4);
      return num.toFixed(6);
    },

    handleGridConfigUpdated(responseData) {
      if (!responseData) return;
      this.gridResultData = responseData.grid_config;
      this.gridStatsData = responseData.stats || null;
      this.handleRangeExplorerConfig(responseData.grid_config);
      // Persist Range Explorer slider inputs so they restore when editing this bot
      if (responseData._range_explorer) {
        this.form.strategy_config._range_explorer = responseData._range_explorer;
      }
      this.form.strategy_config._grid_coupled = this.gridCoupled;
    },

    isGridOverrideField(fieldName) {
      if (!this.gridCoupled) return false;
      if (!this.gridResultData) return false;
      const isLong = this.gridTradingMode === 'LONG';
      const gridKeys = [
        'calculator', 'entry_spacing_dollars', 'exit_spacing_dollars',
        'order_size', 'position_max_size',
        'reference_price', 'trading_mode', 'order_max_per_side',
        'linear_spacing_factor', 'max_order_size',
        'geometric_spacing_factor', 'geometric_scaling_curve',
        // LONG: explorer controls floor, SHORT: explorer controls ceiling
        ...(isLong ? ['range_floor_price'] : ['range_ceiling_price']),
      ];
      return gridKeys.includes(fieldName) && this.gridResultData[fieldName] !== undefined;
    },

    resetState() {
      this.isSubmitting = false;
      // Don't reset isInitializing here - it's controlled by initializeForm
      this.selectedCategory = null;
      this.importedConfig = null;
      this.isImporting = false;
      this.importError = null;
      this.showGridChartMobile = false;
      this.gridResultData = null;
      this.gridStatsData = null;
      this.gridCoupled = true;
      this.form = {
        name: "",
        api_key_id: "",
        api_key_ids: [],
        symbol: "",
        strategy_id: "",
        strategy_config: {},
        account_name: "",
        api_key_description: "",
        auto_start: false,
        leverage: 1,
        account_id: "",
        node_type_preference: "spot",
        log_level: "WARNING",
      };
      this.strategyFields = [];
      // Clear dropdown data to prevent stale state
      this.apiKeys = [];
      this.accounts = [];
      // Close any open dropdowns
      this.apiKeysDropdownOpen = false;
    },

    async fetchApiKeys() {
      if (!this.form.account_id) {
        this.apiKeys = [];
        return;
      }

      // Skip fetching API keys when editing - we already have the necessary info
      if (this.isEditing) {
        // When editing, we only need minimal API key data for display
        // The bot already has api_key_description and api_keys fields
        // Set leverage from the bot itself
        this.form.leverage = this.botToEdit.leverage || 1;
        return;
      }

      try {
        const accountId = this.form.account_id;

        // Use the new lightweight endpoint for creating bots
        const response = await api.get(`/api/api-keys/bot-modal-list/`, {
          params: {
            account_id: accountId,
          },
        });
        this.apiKeys = response.data;
      } catch (error) {
        console.error("Error fetching API keys:", error);
        this.apiKeys = [];
      }
    },

    async fetchStrategies() {
      try {
        const cacheKey = "strategies";
        const response = await this.cachedRequest(cacheKey, () =>
          api.get("/api/strategies/")
        );
        this.strategies = response.data;
      } catch {
        // Error handled silently
      }
    },

    async fetchGlobalSettings() {
      try {
        const response = await api.get("/api/global-settings/");
        this.globalSettings = response.data;
      } catch (error) {
        console.error("Error fetching global settings:", error);
        this.globalSettings = [];
      }
    },

    getGlobalSettingValue(fieldName) {
      // First try to find a setting matching both category and symbol
      const symbolSetting = this.globalSettings.find(
        (setting) =>
          setting.key === fieldName &&
          setting.category === this.getCategory() &&
          setting.symbol === this.form.symbol
      );
      if (symbolSetting) return symbolSetting.value;

      // Then try to find a category-level setting
      const categorySetting = this.globalSettings.find(
        (setting) =>
          setting.key === fieldName &&
          setting.category === this.getCategory() &&
          !setting.symbol
      );
      if (categorySetting) return categorySetting.value;

      // Finally try to find a global setting
      const globalSetting = this.globalSettings.find(
        (setting) =>
          setting.key === fieldName && !setting.category && !setting.symbol
      );
      return globalSetting ? globalSetting.value : null;
    },

    getCategory() {
      // Use selectedCategory if available, otherwise determine from symbol
      if (this.selectedCategory) return this.selectedCategory;
      if (!this.form.symbol) return null;
      return this.form.symbol.includes("USDT") ? "Spot" : "Inverse";
    },

    getCategoryFromSymbol(symbol) {
      // Get category from the symbolPairs store data structure
      if (!symbol || !this.symbolPairs) return null;
      
      // When editing a bot, we need to find which category contains this symbol
      // The bot might have been saved with a specific category that we need to respect
      for (const [category, pairs] of Object.entries(this.symbolPairs)) {
        if (pairs[symbol]) {
          // For editing, if the bot already has a category set, we should check if it matches
          if (this.botToEdit && this.botToEdit.category) {
            // If this is the saved category, use it
            if (category === this.botToEdit.category) {
              return category;
            }
          } else {
            // For new bots or when no category is saved, return the first match
            return category;
          }
        }
      }
      
      // If we're editing and have a category but didn't find the symbol, still return the saved category
      if (this.botToEdit && this.botToEdit.category) {
        return this.botToEdit.category;
      }
      
      return null;
    },

    getSettlementCurrencyFromSymbol(symbol) {
      // Get settlement currency from the symbolPairs store data structure
      if (!symbol || !this.symbolPairs) return null;
      
      // If we have a selected category, use it directly
      if (this.selectedCategory && this.symbolPairs[this.selectedCategory]) {
        const pairs = this.symbolPairs[this.selectedCategory];
        if (pairs[symbol]) {
          return pairs[symbol].settle;
        }
      }
      
      // Fallback: search all categories
      for (const [, pairs] of Object.entries(this.symbolPairs)) {
        if (pairs[symbol]) {
          return pairs[symbol].settle;
        }
      }
      return null;
    },

    async loadStrategyFields() {
      console.log(
        "Loading strategy fields for strategy_id:",
        this.form.strategy_id
      );
      if (this.form.strategy_id) {
        // Clear any strategy error when a strategy is selected
        this.strategyError = "";

        try {
          const cacheKey = `strategy_${this.form.strategy_id}`;
          const response = await this.cachedRequest(cacheKey, () =>
            api.get(`/api/strategies/${this.form.strategy_id}/`)
          );

          console.log("Strategy response:", response.data);
          this.strategyFields = response.data.fields;
          console.log("Raw strategy fields:", this.strategyFields);

          // Ensure strategy_config exists
          if (!this.form.strategy_config) {
            this.form.strategy_config = {};
          }

          // Only set default values for fields that don't exist in the current config
          for (const field of this.strategyFields) {
            // Skip if the field already has a value in the config (including 0)
            if (this.form.strategy_config[field.name] !== undefined) {
              console.log(
                `Preserving existing value for ${field.name}:`,
                this.form.strategy_config[field.name]
              );

              // Special handling for boolean fields
              if (field.type === "boolean") {
                console.log(`Processing boolean field ${field.name}`, {
                  current: this.form.strategy_config[field.name],
                });

                if (
                  this.form.strategy_config[field.name] === 1 ||
                  this.form.strategy_config[field.name] === "1" ||
                  this.form.strategy_config[field.name] === true ||
                  this.form.strategy_config[field.name] === "true" ||
                  this.form.strategy_config[field.name] === "True"
                ) {
                  console.log(`Setting ${field.name} to True`);
                  this.form.strategy_config[field.name] = "True";
                } else {
                  console.log(`Setting ${field.name} to False`);
                  this.form.strategy_config[field.name] = "False";
                }
              }

              continue;
            }

            // For new fields only, set defaults
            if (field.type === "smart") {
              // Add safety check to prevent calling getDefaultFieldValue for fields that don't exist in defaultFields
              const defaultField = this.defaultFields.find(
                (df) => df.name === field.name
              );
              if (!defaultField) continue;  // Skip if not in defaultFields
              
              try {
                const result = await getDefaultFieldValue(
                  field.name,
                  this.strategyFields,
                  this.form.strategy_config,
                  this.botToEdit
                );

                if (result && result.value !== undefined) {
                  console.log(
                    `Setting default value for ${field.name}:`,
                    result.value
                  );
                  this.form.strategy_config[field.name] = result.value;
                }
              } catch (error) {
                console.error(
                  `Error setting default value for ${field.name}:`,
                  error
                );
              }
            } else if (field.type === "dropdown") {
              const options =
                field.options?.split(",").map((opt) => opt.trim()) ||
                field.default_value?.split(",").map((opt) => opt.trim()) ||
                [];
              this.form.strategy_config[field.name] = options[0] || "";
            } else if (field.type === "boolean") {
              // For boolean fields, check if default_value indicates true
              console.log(
                `Setting default for boolean field ${field.name}:`,
                field.default_value
              );

              if (
                field.default_value === "1" ||
                field.default_value === 1 ||
                field.default_value === true ||
                field.default_value === "true" ||
                field.default_value === "True"
              ) {
                this.form.strategy_config[field.name] = "True";
              } else {
                this.form.strategy_config[field.name] = "False";
              }
            } else {
              // Check for global setting value first
              const globalValue = this.getGlobalSettingValue(field.name);
              this.form.strategy_config[field.name] =
                globalValue !== null ? globalValue : field.default_value;
            }
          }
        } catch (error) {
          console.error("Error loading strategy fields:", error);
        }
      }
    },

    getInputType(field) {
      if (field.type === "smart") {
        return {
          type: "number",
          readonly: false,
          class: "input-bordered",
        };
      }

      switch (field.type) {
        case "quantity":
          return {
            type: "number",
            readonly: false,
            min: this.getMinOrderValueForDisplay || 0,
            step: "any",
          };
        case "int":
          return { type: "number", readonly: false };
        case "float":
        case "decimal":
          return { type: "number", readonly: false };
        case "dropdown":
          return {
            type: "dropdown",
            readonly: false,
            options:
              field.options?.split(",").map((opt) => opt.trim()) ||
              field.default_value?.split(",").map((opt) => opt.trim()) ||
              [],
          };
        case "boolean":
          return {
            type: "dropdown",
            readonly: false,
            options: ["True", "False"], // Fixed options
            class: "select-bordered",
          };
        default:
          return { type: "text", readonly: false };
      }
    },

    async submitForm() {
      console.log("Submit form called");

      if (this.isSubmitting) {
        console.log("Form is already submitting, returning");
        return; // Prevent multiple submissions
      }

      // Debug boolean fields
      if (this.strategyFields) {
        const booleanFields = this.strategyFields.filter(
          (f) => f.type === "boolean"
        );
        if (booleanFields.length > 0) {
          console.log(
            "Boolean fields before submission:",
            booleanFields.map((f) => ({
              name: f.name,
              value: this.form.strategy_config[f.name],
            }))
          );
        }
      }

      // Force validation of required fields
      const requiredFields = document.querySelectorAll(
        "select[required], input[required]"
      );
      let isValid = true;

      requiredFields.forEach((field) => {
        if (!field.value) {
          console.log("Required field empty:", field);
          isValid = false;
          field.classList.add("input-error");
        }
      });

      // Check if strategy is selected
      if (!this.form.strategy_id) {
        console.log("No strategy selected, showing error");
        this.strategyError = "Strategy is required. Please select a strategy.";
        // Switch to the Strategy tab to make the error visible
        this.$nextTick(() => {
          console.log("Switching to Strategy tab");
          this.activeTab = "Strategy";
        });
        return;
      }

      if (!isValid) {
        console.log("Form validation failed");
        return;
      }

      this.isSubmitting = true;
      try {
        // Clear any previous errors
        this.strategyError = "";

        // Validate all quantity fields
        const quantityFields = this.strategyFields.filter(
          (field) => field.type === "quantity"
        );

        quantityFields.forEach((field) => this.validateField(field.name));

        if (!this.isFormValid) {
          throw new Error("Please fix the invalid fields before submitting");
        }

        const formData = {
          name: this.form.name,
          symbol: this.form.symbol,
          strategy_id: this.form.strategy_id,
          strategy_config: this.form.strategy_config,
          account_id: this.form.account_id,
          api_key_id: this.form.api_key_id,
          api_key_ids: this.form.api_key_ids,
          auto_start: this.form.auto_start,
          leverage: this.form.leverage,
          node_type_preference: this.form.node_type_preference,
          log_level: this.form.log_level,
          category: this.selectedCategory || this.getCategoryFromSymbol(this.form.symbol) || 'Linear',
          currency: this.getSettlementCurrencyFromSymbol(this.form.symbol) || 'USDT',
          skip_symbol_validation: this.prefilledData?.skipSymbolValidation || false,
        };

        // Debug boolean fields in form data
        if (this.strategyFields) {
          const booleanFields = this.strategyFields.filter(
            (f) => f.type === "boolean"
          );
          if (booleanFields.length > 0) {
            console.log(
              "Boolean fields in form data before preparation:",
              booleanFields.map((f) => ({
                name: f.name,
                value: formData.strategy_config[f.name],
              }))
            );
          }
        }

        const preparedData = this.prepareFormDataForSubmission(formData);

        // Debug boolean fields after preparation
        if (this.strategyFields) {
          const booleanFields = this.strategyFields.filter(
            (f) => f.type === "boolean"
          );
          if (booleanFields.length > 0) {
            console.log(
              "Boolean fields in form data after preparation:",
              booleanFields.map((f) => ({
                name: f.name,
                value: preparedData.strategy_config[f.name],
              }))
            );
          }
        }

        console.log("Submitting form data:", preparedData);
        this.$emit("submit", preparedData);
        // Don't reset isSubmitting here as the parent component should handle closing the modal
      } catch (error) {
        console.error(error.message);
        this.isSubmitting = false;
      }
    },

    async submitFormKeepOpen() {
      console.log("Submit form (keep open) called");

      if (this.isSubmitting) {
        console.log("Form is already submitting, returning");
        return;
      }

      // Check if strategy is selected
      if (!this.form.strategy_id) {
        console.log("No strategy selected, showing error");
        this.strategyError = "Strategy is required. Please select a strategy.";
        this.$nextTick(() => {
          console.log("Switching to Strategy tab");
          this.activeTab = "Strategy";
        });
        return;
      }

      this.isSubmitting = true;
      try {
        this.strategyError = "";

        // Validate all quantity fields
        const quantityFields = this.strategyFields.filter(
          (field) => field.type === "quantity"
        );

        quantityFields.forEach((field) => this.validateField(field.name));

        if (!this.isFormValid) {
          throw new Error("Please fix the invalid fields before submitting");
        }

        const formData = {
          name: this.form.name,
          symbol: this.form.symbol,
          strategy_id: this.form.strategy_id,
          strategy_config: this.form.strategy_config,
          account_id: this.form.account_id,
          api_key_id: this.form.api_key_id,
          api_key_ids: this.form.api_key_ids,
          auto_start: this.form.auto_start,
          leverage: this.form.leverage,
          node_type_preference: this.form.node_type_preference,
          log_level: this.form.log_level,
          category: this.selectedCategory || this.getCategoryFromSymbol(this.form.symbol) || 'Linear',
          currency: this.getSettlementCurrencyFromSymbol(this.form.symbol) || 'USDT',
          skip_symbol_validation: this.prefilledData?.skipSymbolValidation || false,
        };

        const preparedData = this.prepareFormDataForSubmission(formData);

        console.log("Submitting form data (keep open):", preparedData);
        // Emit with keepOpen flag - parent will call resetSubmitting() when done
        this.$emit("submit", preparedData, { keepOpen: true });
      } catch (error) {
        console.error(error.message);
        this.isSubmitting = false;
      }
    },

    setErrors(errors = {}) {
      this.isSubmitting = false;
      // Handle specific field errors if needed
      if (errors.non_field_errors) {
        alert(errors.non_field_errors.join("\n"));
      }
    },

    resetSubmitting() {
      this.isSubmitting = false;
    },

    humanizeFieldName(fieldName) {
      return fieldName
        .split(/[_\s]+|(?=[A-Z])/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    },

    getAccountName(accountId) {
      const account = this.accounts.find((acc) => acc.id === accountId);
      return account ? account.name : "";
    },

    getApiKeyDescription(apiKeyId) {
      const apiKey = this.apiKeys.find((key) => key.id === apiKeyId);
      if (apiKey) {
        return apiKey.description || apiKey.api_key;
      } else {
        return "Unknown API Key";
      }
    },

    async fetchHighestGridLevel() {
      if (!this.isEditing || !this.botToEdit?.id) return;

      try {
        const cacheKey = `grid_levels_${this.botToEdit.id}`;
        const gridLevels = await this.cachedRequest(cacheKey, async () => {
          const response = await api.get(
            `/api/bots/${this.botToEdit.id}/grid_levels/`
          );
          return response.data.grid_levels;
        });

        const activeGridLevels = gridLevels.filter((level) => level.is_active);
        if (activeGridLevels.length > 0) {
          const prices = activeGridLevels.map((level) => Number(level.price));
          this.highestGridPrice = Math.max(...prices);
        } else {
          this.highestGridPrice = null;
        }
      } catch (error) {
        this.highestGridPrice = null;
      }
    },

    isCurrencyField(fieldName) {
      return (
        fieldName.toLowerCase().includes("price") ||
        fieldName.toLowerCase().includes("amount") ||
        fieldName.toLowerCase().includes("range")
      );
    },

    isPercentageField(fieldName) {
      return (
        fieldName.toLowerCase().includes("percentage") ||
        fieldName.toLowerCase().includes("percent")
      );
    },

    async updateSpecialFieldValues() {
      const strategy = this.strategies.find(
        (s) => s.id === this.form.strategy_id
      );
      if (!strategy?.custom_fields) return;

      const specialFields = strategy.custom_fields.filter(
        (f) => f.type === "special"
      );

      // Clear existing values first
      this.specialFieldValues = {};

      // Use Promise.all to fetch all special field values in parallel
      const fieldPromises = specialFields.map(async (field) => {
        try {
          const value = await getSpecialFieldValue(
            field.name,
            this.isEditing ? this.botToEdit?.id : null,
            this.form.symbol,
            this.form.strategy_config
          );

          if (value) {
            this.specialFieldValues[field.name] = value;
          }
        } catch (error) {
          console.error(
            `Error fetching special field value for ${field.name}:`,
            error
          );
        }
      });

      await Promise.all(fieldPromises);
    },

    orderCustomFieldsByDependency(fields) {
      const result = [];
      const visited = new Set();
      const visiting = new Set();

      const visit = (field) => {
        if (visiting.has(field.name)) {
          return; // Skip circular dependencies
        }
        if (visited.has(field.name)) {
          return;
        }

        visiting.add(field.name);

        // Find dependencies in the formula
        const dependencies = fields.filter(
          (f) => field.formula.includes(`{${f.name}}`) && f.name !== field.name
        );

        // Visit dependencies first
        dependencies.forEach((dep) => visit(dep));

        visiting.delete(field.name);
        visited.add(field.name);
        result.push(field);
      };

      fields.forEach((field) => {
        if (!visited.has(field.name)) {
          visit(field);
        }
      });

      return result;
    },

    async fetchSpecialFields() {
      try {
        const fields = await getSpecialFields();
        this.specialFields = fields;
      } catch (error) {
        this.specialFields = [];
      }
    },

    async fetchOpenOrdersCount() {
      if (this.isLoading.orders) return; // Prevent duplicate requests
      this.isLoading.orders = true;

      try {
        const apiKeyId = this.isEditing
          ? this.botToEdit.api_key_id
          : this.form.api_key_id;
        if (!apiKeyId || !this.form.account_id) {
          this.openOrdersCount = null;
          return;
        }

        const cacheKey = `orders_count_${apiKeyId}`;
        this.openOrdersCount = await this.cachedRequest(cacheKey, () =>
          walletService.getOpenOrdersCount(apiKeyId)
        );
      } catch (error) {
        this.openOrdersCount = "?";
      } finally {
        this.isLoading.orders = false;
      }
    },

    async fetchWalletBalance() {
      if (this.isLoading.wallet) return; // Prevent duplicate requests
      this.isLoading.wallet = true;

      try {
        if (!this.form.api_key_id || !this.form.symbol) {
          this.walletBalance = null;
          return;
        }

        const walletInfo = await walletService.getWalletBalance(
          this.form.api_key_id,
          this.form.symbol
        );
        this.walletBalance = walletInfo[walletInfo.currency] || 0;
      } catch (error) {
        console.error("Error fetching wallet balance:", error);
        this.walletBalance = null;
      } finally {
        this.isLoading.wallet = false;
      }
    },

    calculateSmartField(fieldName, formula, config) {
      try {
        // Replace field references with actual values
        let calculatedFormula = formula;
        Object.entries(config).forEach(([key, value]) => {
          calculatedFormula = calculatedFormula.replace(
            new RegExp(`\\{${key}\\}`, "g"),
            value
          );
        });

        // Evaluate the formula
        const result = new Function(`return ${calculatedFormula}`)();
        return result.toString();
      } catch (error) {
        console.error(`Error calculating ${fieldName}:`, error);
        return "0";
      }
    },

    async updateSmartFields() {
      if (this.isEditing) return;

      // Create an array of promises to handle all field updates
      const updatePromises = this.strategyFields.map(async (field) => {
        if (field.type === "smart") {
          const defaultField = this.defaultFields.find(
            (df) => df.name === field.name
          );
          if (!defaultField) return;

          try {
            // Set loading state for this field
            this.loadingSmartFields[field.name] = true;

            const result = await getDefaultFieldValue(
              field.name,
              this.form.strategy_config,
              {
                apiKeyId: this.form.api_key_id,
                symbol: this.form.symbol,
              }
            );

            if (result && result.value !== undefined) {
              this.form.strategy_config[field.name] = result.value;
            }
          } catch (error) {
            console.error(
              `Error calculating smart field ${field.name}:`,
              error
            );
          } finally {
            // Clear loading state
            this.loadingSmartFields[field.name] = false;
          }
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);
    },

    async fetchMinOrderValue() {
      if (!this.form.symbol) return;
      try {
        console.log("Fetching min order value for:", this.form.symbol);
        this.minOrderValue = await instrumentInfoService.getMinOrderValue(
          this.form.symbol,
          this.getCategory()
        );
        console.log("Fetched min order value:", this.minOrderValue);

        // Validate all quantity fields when min order qty changes
        this.strategyFields
          .filter((field) => field.type === "quantity")
          .forEach((field) => this.validateField(field.name));
      } catch (error) {
        console.error("Error fetching minimum order value:", error);
        this.minOrderValue = null;
      }
    },

    isQuantityFieldInvalid(fieldName) {
      return this.invalidFields.has(fieldName);
    },

    validateField(fieldName) {
      const field = this.strategyFields.find((f) => f.name === fieldName);
      if (field?.type !== "quantity") return;

      const value = parseFloat(this.form.strategy_config[fieldName]);
      const minValue = this.getMinOrderValueForDisplay;

      console.log(`Validating ${fieldName}:`, { value, minValue });

      if (!minValue || isNaN(minValue)) {
        console.warn("No minimum quantity available for validation");
        return;
      }

      if (isNaN(value) || value < minValue) {
        this.invalidFields.add(fieldName);
      } else {
        this.invalidFields.delete(fieldName);
      }
    },

    async cachedRequest(key, requestFn) {
      // Check if request is in progress
      if (this.requestInProgress.get(key)) {
        return this.requestInProgress.get(key);
      }

      // Check cache
      const cached = this.requestCache.get(key);
      if (cached) {
        return cached;
      }

      // Set request in progress
      const promise = requestFn();
      this.requestInProgress.set(key, promise);

      try {
        const result = await promise;
        // Cache the result
        this.requestCache.set(key, result);
        return result;
      } finally {
        this.requestInProgress.delete(key);
      }
    },

    // Format boolean values from numeric (0/1) to string ("True"/"False") for display
    formatBooleanValuesForDisplay() {
      for (const field of this.strategyFields) {
        if (
          field.type === "boolean" &&
          this.form.strategy_config[field.name] !== undefined
        ) {
          // Convert to "True" or "False" using the utility function
          const rawValue = this.form.strategy_config[field.name];
          this.form.strategy_config[field.name] = isTrueValue(rawValue)
            ? "True"
            : "False";

          // Log the conversion for debugging
          console.log(
            `Formatting boolean field ${field.name}: ${rawValue} -> ${
              this.form.strategy_config[field.name]
            }`
          );
        }
      }
    },

    prepareFormDataForSubmission(formData) {
      // Make a deep copy to avoid modifying the original
      const preparedData = JSON.parse(JSON.stringify(formData));

      // Handle api_key_id - if it's empty string, set to null for backend
      if (preparedData.api_key_id === "" || preparedData.api_key_id === undefined) {
        preparedData.api_key_id = null;
      }

      // If api_key_ids is provided but api_key_id is not, backend will use first from list
      // This handles the multi-API key selection case
      if (preparedData.api_key_ids && preparedData.api_key_ids.length > 0 && !preparedData.api_key_id) {
        // Backend will handle this, but we can also set it explicitly
        preparedData.api_key_id = preparedData.api_key_ids[0];
      }

      // If api_key_id is set but api_key_ids is empty, populate api_key_ids from api_key_id
      // This handles the single radio button selection case
      if (preparedData.api_key_id && (!preparedData.api_key_ids || preparedData.api_key_ids.length === 0)) {
        preparedData.api_key_ids = [preparedData.api_key_id];
      }

      // Process boolean fields
      if (this.strategyFields && preparedData.strategy_config) {
        for (const field of this.strategyFields) {
          if (
            field.type === "boolean" &&
            preparedData.strategy_config[field.name] !== undefined
          ) {
            // Convert to 1 or 0 using the utility function
            const rawValue = preparedData.strategy_config[field.name];
            const boolValue = toBooleanValue(rawValue);
            preparedData.strategy_config[field.name] = boolValue;

            // Log the conversion for debugging
            console.log(
              `Converting boolean field ${field.name} for submission: ${rawValue} -> ${boolValue}`
            );
          }
        }
      }

      return preparedData;
    },

    triggerImport() {
      // Trigger hidden file input
      this.$refs.importFileInput.click();
    },

    async handleImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Reset import state
      this.importError = null;

      // Validate file extension
      if (!file.name.toLowerCase().endsWith('.json')) {
        const errorMsg = 'Please select a JSON file';
        this.importError = errorMsg;
        this.showNotification(errorMsg, 'error');
        event.target.value = '';
        return;
      }

      // Validate file size (1MB max)
      const MAX_FILE_SIZE = 1024 * 1024; // 1MB
      if (file.size > MAX_FILE_SIZE) {
        const errorMsg = 'File too large (max 1MB)';
        this.importError = errorMsg;
        this.showNotification(errorMsg, 'error');
        event.target.value = '';
        return;
      }

      this.isImporting = true;

      try {
        // Read file
        const fileContent = await this.readFileAsText(file);
        let importedData;

        try {
          importedData = JSON.parse(fileContent);
        } catch (parseError) {
          const errorMsg = 'Invalid JSON file format';
          this.importError = errorMsg;
          this.showNotification(errorMsg, 'error');
          return;
        }

        // Validate with backend
        const response = await api.post('/api/bots/import-config/', {
          config: importedData,
          account_id: this.form.account_id,
          api_key_ids: this.form.api_key_ids,
        });

        if (response.data.valid) {
          // Populate form with imported config
          this.applyImportedConfig(response.data.config);
          this.showNotification('Configuration imported successfully', 'success');
        }
      } catch (error) {
        console.error('Import failed:', error);
        const errorMsg = error.response?.data?.error || 'Failed to import configuration';
        this.importError = errorMsg;
        this.showNotification(errorMsg, 'error');
      } finally {
        this.isImporting = false;
        // Reset file input (use null for better browser compatibility)
        event.target.value = null;
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });
    },

    applyImportedConfig(config) {
      // Set flag to prevent leverage from being overwritten
      this.importedConfig = config;

      // Populate form with imported values
      this.form.name = config.name;
      this.form.symbol = config.symbol;
      this.form.leverage = parseFloat(config.leverage);
      this.form.node_type_preference = config.node_type_preference;
      this.form.log_level = config.log_level;
      this.form.strategy_id = config.strategy_id;
      this.form.strategy_config = { ...config.strategy_config };

      // Set selected category for symbol dropdown
      this.selectedCategory = config.category;

      // Trigger strategy field loading
      this.$nextTick(() => {
        this.loadStrategyFields();
      });
    },

    async exportConfig() {
      if (!this.botToEdit) return;

      try {
        const response = await api.get(
          `/api/bots/${this.botToEdit.id}/export-config/`,
          { responseType: 'blob' }
        );

        // Create download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const filename = `bot_config_${this.botToEdit.id}_${this.botToEdit.name.replace(/ /g, '_')}.json`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.showNotification('Configuration exported successfully', 'success');
      } catch (error) {
        console.error('Export failed:', error);
        this.showNotification('Failed to export configuration', 'error');
      }
    },

    showNotification(message, type = 'info') {
      // Emit notification to parent component
      this.$emit('notification', { message, type });
    },
  },
};
</script>

<style scoped>
/* Default behavior for mobile: centered modal (default modal behavior) */
.top-anchored-modal {
  /* No custom alignment on mobile - will use modal's default centering */
}

/* Desktop behavior: top-anchored modal */
@media (min-width: 768px) {
  .top-anchored-modal {
    align-items: flex-start;
    padding-top: 8rem;
  }
  .top-anchored-modal:has(.grid-mode-active) {
    padding-top: 0.5rem;
  }
}

/* Remove number input spinners */
.no-scrollbar::-webkit-outer-spin-button,
.no-scrollbar::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
